import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { 
  fetchAndFormatMessages,
  sendAndFormatMessage,
  subscribeToFormattedMessages,
  markSpecificMessagesAsRead,
  deleteMessage as deleteMessageService,
  formatMessage
} from '@/services/messageService';
import { sendBookingRequest as sendBookingRequestService } from '@/services/bookingService';
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getProfileName } from '@/services/messageService';

export interface UIMessage {
  id: string;
  senderId: string;
  recipientId: string;
  content: string;
  timestamp: string;
  isSent: boolean;
  isDelivered: boolean;
  isRead: boolean;
  isBookingRequest: boolean;
  contentType: string;
  attachmentUrl?: string | null;
  attachmentType?: string | null;
  senderName?: string;
  senderType?: 'user' | 'escort' | 'ai';
}

interface UseMessagesReturn {
  messages: UIMessage[];
  isLoading: boolean;
  sendMessage: (content: string) => Promise<void>;
  sendBookingRequest: (bookingDate: Date, timeSlot: string, duration?: number) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  fetchMessages: () => Promise<void>;
}

export const useMessages = (conversationId?: string, partnerId?: string): UseMessagesReturn => {
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const _fetchMessages = useCallback(async () => {
    if (!user || !partnerId || !conversationId) {
      setMessages([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const formattedMessages = await fetchAndFormatMessages(user.id, partnerId, conversationId);
      setMessages(formattedMessages as UIMessage[]);
    } catch (error) {
      console.error('Error fetching messages in hook:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les messages',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, partnerId, conversationId, toast]);

  useEffect(() => {
    let channel: RealtimeChannel | null = null;
    let isSubscribed = true; // Pour gérer les updates après le unmount

    if (user && partnerId && conversationId) {
      _fetchMessages();

      const handleRealtimeEvent = async (payload: any) => {
        if (!isSubscribed) return; // Si le composant est démonté, ne pas mettre à jour l'état
        
        const { eventType, new: newRecord, old: oldRecord } = payload;

        if (eventType === 'INSERT') {
          const newMessage = newRecord as any;
          
          // Vérifier si le message concerne bien cette conversation (sécurité supplémentaire)
          if (newMessage.conversation_id !== conversationId) {
            return;
          }
          
          // Éviter d'ajouter son propre message une deuxième fois (il est ajouté via sendMessage)
           if (newMessage.sender_id === user?.id) {
               // Optionnel: Mettre à jour le statut local du message envoyé si nécessaire (ex: isDelivered)
               // Mais l'ajout principal se fait dans sendMessage
               return;
           }

          // Faire l'appel asynchrone en dehors de setState
          try {
            const senderName = await getProfileName(newMessage.sender_id);
            const profilesCache = new Map([[newMessage.sender_id, senderName]]);
            const formattedMessage = formatMessage(newMessage, profilesCache);
            
            // Vérifier à nouveau si le composant est monté avant de mettre à jour l'état
            if (isSubscribed) {
              setMessages(prevMessages => {
                // Vérifier à nouveau si le message n'existe pas déjà (double sécurité)
                if (prevMessages.some(msg => msg.id === newMessage.id)) {
                  return prevMessages;
                }
                return [...prevMessages, formattedMessage].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()); // Assurer l'ordre
              });
              
               // Marquer comme lu si la fenêtre est active et que ce n'est pas notre message
               if (document.visibilityState === 'visible' && document.hasFocus() && newMessage.sender_id !== user?.id && !newMessage.is_read) {
                   console.log(`[Realtime] Marking incoming message ${newMessage.id} as read.`);
                   markSpecificMessagesAsRead([newMessage.id]).catch(err => {
                       console.error("[Realtime] Failed to mark message as read:", err);
                   });
               }
            }
          } catch (error) {
            console.error('Error formatting new realtime message:', error);
          }
        } else if (eventType === 'UPDATE') {
          const updatedMessage = newRecord as any;
          // Vérifier si le message concerne bien cette conversation
          if (updatedMessage.conversation_id !== conversationId) {
             return;
          }
          try {
            const senderName = await getProfileName(updatedMessage.sender_id);
            const profilesCache = new Map([[updatedMessage.sender_id, senderName]]);
            const formattedUpdatedMessage = formatMessage(updatedMessage, profilesCache);
            
            if (isSubscribed) {
              setMessages(prev => 
                prev.map(msg => msg.id === updatedMessage.id ? formattedUpdatedMessage : msg)
                   .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) // Maintenir l'ordre
              );
            }
          } catch (error) {
            console.error('Error formatting updated realtime message:', error);
          }
        } else if (eventType === 'DELETE') {
          const deletedMessageId = oldRecord?.id;
           // Vérifier si l'enregistrement supprimé concerne bien cette conversation
           // Note: oldRecord might not contain conversation_id depending on REPLICA IDENTITY
          if (deletedMessageId && isSubscribed) {
             // On suppose que si on reçoit l'event sur ce canal, c'est pertinent
            setMessages(prev => prev.filter(msg => msg.id !== deletedMessageId));
          }
        }
      };

      channel = subscribeToFormattedMessages(conversationId, handleRealtimeEvent);
    }

    return () => {
      isSubscribed = false; // Marquer comme démonté
      if (channel) {
        supabase.removeChannel(channel).catch(err => 
          console.error("Error removing messages channel:", err)
        );
      }
    };
  }, [user, partnerId, conversationId, _fetchMessages]); // Retiré messages des dépendances

  const sendMessage = useCallback(async (content: string, isBookingRequest: boolean = false, contentType: string = 'text') => {
    if (!content.trim() || !partnerId || !user || !conversationId) return;
    
    try {
      await sendAndFormatMessage(
        content, 
        user.id, 
        partnerId, 
        conversationId, 
        isBookingRequest,
        contentType
      );
    } catch (error) {
      console.error('Error sending message in hook:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'envoyer le message',
        variant: 'destructive'
      });
      throw error;
    }
  }, [user, partnerId, conversationId, toast]);

  const sendBookingRequest = useCallback(async (bookingDate: Date, timeSlot: string, duration: number = 60) => {
    if (!partnerId || !user || !conversationId) return;
    
    try {
      const appointmentDate = new Date(bookingDate);
      const [hours, minutes] = timeSlot.split(':').map(Number);
      appointmentDate.setHours(hours, minutes);
      
      await sendBookingRequestService(user.id, partnerId, appointmentDate, timeSlot, duration);
      
      const messageContent = `Demande de rendez-vous pour le ${appointmentDate.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })} à ${timeSlot} (durée: ${duration} minutes)`;
      
      await sendMessage(messageContent, true, 'booking_request');
      
      toast({ title: 'Demande envoyée', description: 'Votre demande de réservation a été envoyée.' });
    } catch (error) {
      console.error('Error sending booking request in hook:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'envoyer la demande de réservation',
        variant: 'destructive'
      });
    }
  }, [user, partnerId, conversationId, toast, sendMessage]);

  const deleteMessage = useCallback(async (messageId: string) => {
    if (!user) return;

    const originalMessages = messages;
    setMessages(prev => prev.filter(msg => msg.id !== messageId));

    try {
      const { success, error } = await deleteMessageService(messageId);
      if (!success) {
        throw error || new Error('Failed to delete message via service.');
      }
      toast({ title: 'Succès', description: 'Message supprimé.' });

    } catch (error: any) {
      console.error('Error deleting message in hook:', error);
      toast({
        title: 'Erreur',
        description: `Impossible de supprimer le message: ${error.message || 'Erreur inconnue'}`,
        variant: 'destructive'
      });
      setMessages(originalMessages);
    }
  }, [user, messages, toast]);

  return {
    messages,
    isLoading,
    sendMessage,
    sendBookingRequest,
    deleteMessage,
    fetchMessages: _fetchMessages
  };
};
